import { APPLE_CUSTOMER_CLIENT_ID, APPLE_REDIRECT_PATH } from './constants';

/**
 * Sign in with Apple on the web, via Apple's official "Sign in with Apple JS"
 * SDK in **popup mode**.
 *
 * Why popup and not the redirect flow
 * -----------------------------------
 * Apple mandates `response_mode=form_post` whenever the requested scope
 * includes `name` or `email` — meaning Apple **POSTs** the result to the
 * registered return URL. Cloudflare Pages serves static assets for GET/HEAD
 * only and answers any other method with 405, so a prerendered
 * `/auth/apple-redirect` page could never receive that POST.
 *
 * With `usePopup: true` the SDK overrides the response mode to `web_message`:
 * the popup hands the authorization back to the opener via `postMessage` and
 * the return URL is never navigated to at all. That removes the POST problem
 * entirely, and matches how both existing providers already work — Google via
 * `google.accounts.oauth2.initCodeClient` popup, Microsoft via MSAL
 * `loginPopup`. `_headers` already sets
 * `Cross-Origin-Opener-Policy: same-origin-allow-popups` for exactly this.
 *
 * `redirectURI` still has to be supplied and still has to be one of the return
 * URLs registered on the Services ID — and in popup mode its **origin must
 * equal `window.location.origin`**, or Apple's popup completes and the SDK's
 * promise never settles. `https://capsulechichub.com/auth/apple-redirect` is
 * registered, so this only resolves on the production origin (preview
 * deployments and localhost will fail at Apple's end, same as they do for
 * Microsoft).
 *
 * Scope is `email` only. Apple never puts the user's name in the identity
 * token — it arrives once, out of band, in the popup's `user` field on first
 * authorization — and the Worker's `/sso/apple` handler deliberately ignores
 * it (Apple customers start with a null name and set it in the profile
 * screen). Asking for `name` would therefore buy nothing while widening the
 * consent sheet.
 */

const APPLE_SDK_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

interface AppleAuthorization {
  id_token?: string;
  code?: string;
  state?: string;
}
interface AppleSignInResponse {
  authorization?: AppleAuthorization;
}
interface AppleIdAuth {
  init: (config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    state: string;
    usePopup: boolean;
  }) => void;
  signIn: () => Promise<AppleSignInResponse>;
}
declare global {
  interface Window {
    AppleID?: { auth: AppleIdAuth };
  }
}

/** Thrown when the user dismisses Apple's sheet. Callers must treat it as a no-op. */
export class AppleSignInCancelledError extends Error {
  readonly name = 'AppleSignInCancelledError';
}

/**
 * Thrown when the browser refused to open Apple's popup. Deliberately a
 * separate type from `AppleSignInCancelledError`: dismissal is silent by
 * design, but this one has to speak — the user did nothing wrong and cannot
 * discover the fix (allow popups) on their own. Mirrors how the Microsoft
 * handler surfaces MSAL's `popup_window_error` as a toast.
 */
export class AppleSignInPopupBlockedError extends Error {
  readonly name = 'AppleSignInPopupBlockedError';
}

// Apple reports dismissal and a handful of benign interruptions through a
// rejected promise carrying an `error` string rather than a real Error. None
// of these deserve a red error message — the user simply changed their mind.
const CANCELLED_CODES = new Set([
  'popup_closed_by_user',
  'user_cancelled_authorize',
  'user_trigger_new_signin_flow',
]);

// Not a cancellation: the popup never opened, so the user never got a choice.
const POPUP_BLOCKED_CODES = new Set(['popup_blocked_by_browser']);

// The SDK rejects with a plain object (`{ error: 'popup_closed_by_user' }`),
// not an `Error`, so `instanceof` checks are useless here — hence classifying
// off the `error` string. Some call sites (and Apple's own docs) surface the
// bare code, so accept that shape too.
function appleErrorCode(err: unknown): string | null {
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && 'error' in err) {
    const code = (err as { error?: unknown }).error;
    if (typeof code === 'string') return code;
  }
  return null;
}

/**
 * True when an Apple SDK rejection means "the user backed out" rather than
 * "something broke". Exported for tests.
 */
export function isAppleCancellation(err: unknown): boolean {
  const code = appleErrorCode(err);
  return code !== null && CANCELLED_CODES.has(code);
}

/**
 * True when the browser blocked Apple's popup. Exported for tests. Kept
 * distinct from `isAppleCancellation` so the UI can guide the user instead of
 * failing silently or showing a bare "Sign-in failed".
 */
export function isApplePopupBlocked(err: unknown): boolean {
  const code = appleErrorCode(err);
  return code !== null && POPUP_BLOCKED_CODES.has(code);
}

/**
 * Pull the identity token out of an Apple popup response, asserting the
 * `state` we generated came back untouched. Apple's SDK does not guarantee an
 * `id_token` is present (a misconfigured Services ID can yield a `code`-only
 * response), so an explicit check beats letting `undefined` reach the Worker
 * and coming back as an opaque 400.
 */
export function extractAppleIdToken(
  response: AppleSignInResponse | undefined,
  expectedState: string,
): string {
  const auth = response?.authorization;
  if (!auth) {
    throw new Error('Apple sign-in returned no authorization');
  }
  if (auth.state !== expectedState) {
    throw new Error('Apple sign-in state mismatch');
  }
  if (!auth.id_token) {
    throw new Error('Apple sign-in did not return an identity token');
  }
  return auth.id_token;
}

// Module-scoped so re-opening the sign-in modal reuses the resolved promise.
// Attaching load listeners to a <script> whose load event already fired would
// hang forever — the same trap the GSI loader documents in SignInOptions.
let sdkPromise: Promise<void> | null = null;

function loadAppleSdk(): Promise<void> {
  if (window.AppleID?.auth) return Promise.resolve();
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = APPLE_SDK_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => {
      sdkPromise = null;
      reject(new Error('Apple sign-in failed to load'));
    };
    document.head.appendChild(el);
  });
  return sdkPromise;
}

/**
 * Fresh 128-bit hex `state` for each authorization, checked on the way back by
 * `extractAppleIdToken`. Exported for tests: a constant (or otherwise
 * predictable) state would silently defeat that check while every other
 * assertion in the suite stayed green, and it is the one seam here where a
 * quiet regression carries security meaning.
 */
export function randomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Run the Apple popup and return the identity token for `POST /sso/apple`.
 * Throws `AppleSignInCancelledError` on dismissal.
 */
/**
 * The env-var gate hides the Apple button entirely when the Services ID is
 * unset — unlike the missing Google client id, which renders a loud red
 * "not configured yet". That silence is intentional (it sequences the
 * rollout) but invisible: no log, no failing test, no failing build. Warn in
 * dev so anyone running the app can see why the button isn't there.
 */
export function warnIfAppleNotConfigured(): void {
  if (!import.meta.env.DEV || APPLE_CUSTOMER_CLIENT_ID) return;
  console.warn(
    '[auth] Apple sign-in button hidden: VITE_APPLE_CUSTOMER_CLIENT_ID is unset. ' +
      'Set it to the Services ID (com.capsulechichub.web) to show the button.',
  );
}

export async function signInWithApple(): Promise<string> {
  if (!APPLE_CUSTOMER_CLIENT_ID) {
    throw new Error(
      'Apple sign-in is not configured (VITE_APPLE_CUSTOMER_CLIENT_ID missing)',
    );
  }
  await loadAppleSdk();
  const appleId = window.AppleID;
  if (!appleId) throw new Error('Apple sign-in failed to load');

  const state = randomState();
  appleId.auth.init({
    clientId: APPLE_CUSTOMER_CLIENT_ID,
    scope: 'email',
    redirectURI: `${window.location.origin}${APPLE_REDIRECT_PATH}`,
    state,
    usePopup: true,
  });

  let response: AppleSignInResponse;
  try {
    response = await appleId.auth.signIn();
  } catch (err) {
    if (isAppleCancellation(err)) {
      throw new AppleSignInCancelledError('Apple sign-in was cancelled');
    }
    if (isApplePopupBlocked(err)) {
      throw new AppleSignInPopupBlockedError('Apple sign-in popup was blocked');
    }
    throw err instanceof Error ? err : new Error('Apple sign-in failed');
  }
  return extractAppleIdToken(response, state);
}
